<script>
/* eslint no-console: 0 */
import globalConfigMixin from 'shared/mixins/globalConfigMixin';

export default {
  mixins: [globalConfigMixin],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    classObject() {
      return 'w-full';
    },
    activeIndex() {
      const idx = this.items.findIndex(i => i.route === this.$route.name);
      if (idx !== -1) return idx;
      return this.items.length > 0 ? this.items.length - 1 : 0;
    },
    currentStep() {
      return this.items[this.activeIndex] || null;
    },
  },
  methods: {
    isActive(item) {
      return this.items.indexOf(item) === this.activeIndex;
    },
    isOver(item) {
      return this.items.indexOf(item) < this.activeIndex;
    },
    isArrowCompleted(index) {
      return index < this.activeIndex;
    },
    getArrowColor(index) {
      return this.isArrowCompleted(index) ? '#22c55e' : '#cbd5e1';
    },
  },
};
</script>

<template>
  <div class="wizard-container w-full">
    <!-- Desktop / Tablet wrapper -->
    <div class="hidden md:block">
      <transition-group
        name="wizard-items"
        tag="div"
        class="wizard-box"
        :class="classObject"
      >
        <div
          v-for="(item, index) in items"
          :key="`desktop-${item.route}`"
          class="item-wrapper"
        >
          <div
            class="item"
            :class="{
              active: isActive(item),
              over: isOver(item),
            }"
          >
            <span class="step">
              {{ items.indexOf(item) + 1 }}
            </span>
            <h3
              class="text-slate-800 dark:text-n-gray-7 text-base font-medium overflow-hidden whitespace-nowrap text-ellipsis leading-tight"
            >
              {{ item.title }}
            </h3>
            <p class="!pl-0 m-0 text-sm text-n-gray-5">
              {{ item.body }}
            </p>
          </div>

          <div v-if="index < items.length - 1" class="arrow-connector">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern :id="`dotted-line-${index}`" x="0" y="0" width="4" height="1" patternUnits="userSpaceOnUse">
                  <rect width="2" height="1" :fill="getArrowColor(index)"/>
                </pattern>
              </defs>
              <line x1="0" y1="10" x2="15" y2="10" :stroke="`url(#dotted-line-${index})`" stroke-width="2" />
              <path d="M15 6 L17 10 L15 14" :stroke="getArrowColor(index)" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </div>
      </transition-group>
    </div>

    <!-- Mobile wrapper -->
    <div class="block md:hidden">
      <transition-group
        name="wizard-items"
        tag="div"
        class="wizard-box justify-start"
        :class="classObject"
      >
        <div v-if="currentStep" :key="`mobile-${currentStep.route}`" class="item-wrapper">
          <div class="arrow-connector">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="mobile-dotted-line" x="0" y="0" width="4" height="1" patternUnits="userSpaceOnUse">
                  <rect width="2" height="1" fill="#22c55e"/>
                </pattern>
              </defs>
              <line x1="0" y1="10" x2="15" y2="10" stroke="url(#mobile-dotted-line)" stroke-width="2" />
              <path d="M15 6 L17 10 L15 14" stroke="#22c55e" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div
            class="item mobile-item flex !flex-row !items-center !gap-[6px]"
            :class="{ active: true }"
          >
            <span class="step">{{ activeIndex + 1 }}</span>
            <div class="flex flex-col items-start justify-center gap-[6px]">
              <h3 class="text-slate-800 dark:text-n-gray-7 text-base font-medium overflow-hidden whitespace-nowrap text-ellipsis leading-tight">
                {{ currentStep.title }}
              </h3>
              <p class="!pl-0 m-0 text-sm text-left text-slate-600 dark:text-n-gray-5">
                {{ currentStep.body }}
              </p>
            </div>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-box { display: flex; flex-direction: row; justify-content: center; gap: 0; }
@media (min-width: 768px) { .wizard-box { align-items: flex-start; } }
@media (max-width: 767px) { .wizard-box { align-items: center; } }

.item-wrapper { display: flex; }
@media (min-width: 768px) { .item-wrapper { align-items: flex-start; } }
@media (max-width: 767px) { .item-wrapper { align-items: center; } }

.item {
  @apply px-0 py-4 md:px-2 relative flex flex-col items-center justify-start text-center;
  min-width: 170px; max-width: 170px;
  cursor: default;
}
@media (min-width: 768px) {
  .item { height: 170px; }
  .item .step { @apply mb-4; }
  .item h3 { @apply mb-2; }
  .item p { @apply flex-1; }
}
@media (max-width: 767px) {
  .item { height: auto; min-width: 0; max-width: none; }
}
.item.active h3 { @apply text-grass-500 dark:text-grass-500; }
.item.active .step { @apply bg-grass-500 dark:bg-grass-500 text-white dark:text-white; }
.item.active p { @apply text-n-gray-5 dark:text-white; }

.item.over .step { @apply bg-grass-500 dark:bg-grass-500 text-white dark:text-white; }
.item.over h3 { @apply text-grass-500 dark:text-grass-500; }
.item.over p { @apply text-n-gray-5 dark:text-white; }

.item .step {
  @apply bg-slate-75 dark:bg-n-vars-black-bd/80 rounded-full font-medium w-8 h-8 flex items-center justify-center text-center text-white dark:text-n-gray-5 text-sm mb-2;
}

.arrow-connector { @apply flex items-center justify-center mx-2; }
@media (min-width: 768px) { .arrow-connector { align-self: flex-start; margin-top: 60px; } }

@media (max-width: 767px) {
  .wizard-box { justify-content: flex-start; }
  .mobile-item { @apply justify-start text-left; width: auto !important; min-width: 0 !important; max-width: none !important; height: auto !important; }
  .mobile-item .step { @apply bg-grass-500 dark:bg-grass-500 text-white dark:text-white; margin: 0 !important; flex-shrink: 0; padding: 0 !important; }
}
</style>